import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ReviewReply } from "@/components/ReviewReply";
import { Calendar, Clock, User, ShieldCheck, KeyRound, Star, CheckCircle2, FileText, CreditCard, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-800",
  in_progress: "bg-indigo-100 text-indigo-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-neutral-200 text-neutral-600 line-through",
};

function Stars({ id, value, onChange }) {
  return (
    <div className="flex items-center gap-1 mt-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => onChange(n)} data-testid={`rate-${id}-${n}`} type="button" aria-label={`${n} stars`}>
          <Star className={`w-5 h-5 transition-colors ${n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`} />
        </button>
      ))}
    </div>
  );
}

function ReviewBox({ id, value, onChange, optional }) {
  return (
    <div className="mt-3">
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} maxLength={400} rows={3}
        placeholder={optional ? "Add a short review (optional) — what went well?" : "Share a few words about your experience…"}
        className="rounded-sm text-sm max-w-xl" data-testid={`review-text-${id}`} />
      <p className="text-xs text-muted-foreground mt-1">{value.length}/400 · Great reviews may be featured on the BookdHUB home page.</p>
    </div>
  );
}

const postReview = (bookingId, rating, comment) => api.post("/reviews", { booking_id: bookingId, rating, comment: comment.trim() });

function ConfirmRow({ booking, onDone }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    if (comment.trim() && !rating) { toast.error("Pick a star rating to go with your review."); return; }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/confirm`, { rating: rating || undefined });
      if (comment.trim()) await postReview(booking.id, rating, comment);
      toast.success(comment.trim() ? "Thanks — service confirmed and review posted!" : "Thanks — service confirmed!");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); onDone(); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`confirm-row-${booking.id}`}>
      <p className="text-sm font-medium">Confirm this service was completed</p>
      <Stars id={booking.id} value={rating} onChange={setRating} />
      <ReviewBox id={booking.id} value={comment} onChange={setComment} optional />
      <Button size="sm" className="rounded-sm mt-3" onClick={confirm} disabled={busy} data-testid={`confirm-booking-${booking.id}`}>
        <CheckCircle2 className="w-4 h-4 mr-1" />Confirm completed
      </Button>
    </div>
  );
}

function ReviewRow({ booking, onDone }) {
  const [rating, setRating] = useState(booking.proof?.rating || 0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!rating) { toast.error("Pick a star rating first."); return; }
    if (comment.trim().length < 3) { toast.error("Please write a few words about your experience."); return; }
    setBusy(true);
    try {
      await postReview(booking.id, rating, comment);
      toast.success("Thanks for your review!");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`review-row-${booking.id}`}>
      <p className="text-sm font-medium flex items-center gap-1.5"><MessageSquareQuote className="w-4 h-4 text-primary" />How was {booking.subscriber_name}? Leave a review</p>
      <Stars id={booking.id} value={rating} onChange={setRating} />
      <ReviewBox id={booking.id} value={comment} onChange={setComment} />
      <Button size="sm" className="rounded-sm mt-3" onClick={submit} disabled={busy} data-testid={`submit-review-${booking.id}`}>
        {busy ? "Posting…" : "Post review"}
      </Button>
    </div>
  );
}

function ReviewDisplay({ booking }) {
  const r = booking.review;
  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`review-display-${booking.id}`}>
      <p className="text-xs text-muted-foreground">Your review</p>
      <div className="flex gap-0.5 mt-1">
        {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />)}
      </div>
      <p className="text-sm mt-1 text-foreground/90">“{r.comment}”</p>
      <ReviewReply reply={r.reply} provider={booking.subscriber_name} className="mt-2 max-w-xl" testid={`review-reply-${booking.id}`} />
    </div>
  );
}

export default function CustomerBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const confirmedRef = useRef(false);

  const load = () => api.get("/bookings/mine").then((r) => setBookings(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const bid = params.get("secure_confirm");
    const sid = params.get("session_id");
    if (bid && sid && !confirmedRef.current) {
      confirmedRef.current = true;
      api.post(`/bookings/${bid}/secure-balance/confirm`, { session_id: sid })
        .then(() => { toast.success("Card saved — your provider can charge the balance at check-in."); load(); })
        .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
        .finally(() => { params.delete("secure_confirm"); params.delete("session_id"); setParams(params, { replace: true }); });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const secureBalance = async (id) => {
    try {
      const { data } = await api.post(`/bookings/${id}/secure-balance/setup`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const cancel = async (id) => {
    try {
      await api.put(`/bookings/${id}/status`, { status: "cancelled" });
      toast.success("Booking cancelled");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const payBalance = async (id) => {
    try {
      const { data } = await api.post(`/bookings/${id}/settlement/checkout`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadInvoice = async (id) => {
    try {
      const res = await api.get(`/bookings/${id}/invoice.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Couldn't download the invoice."); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-display text-4xl font-semibold tracking-tighter">My bookings</h1>
      <p className="text-muted-foreground mt-2">Your upcoming and past appointments.</p>

      {loading ? <p className="text-muted-foreground mt-10">Loading…</p> :
        bookings.length === 0 ? (
          <div className="mt-10 border border-dashed border-border rounded-sm p-12 text-center" data-testid="bookings-empty">
            <p className="text-muted-foreground">No bookings yet.</p>
            <Link to="/browse"><Button className="mt-4 rounded-sm" data-testid="browse-cta">Browse services</Button></Link>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {bookings.map((b) => {
              const active = ["pending", "confirmed", "in_progress"].includes(b.status);
              const needsConfirm = b.status === "completed" && !(b.proof?.customer_confirmed);
              return (
                <div key={b.id} data-testid={`booking-${b.id}`} className="bg-white border border-border rounded-sm p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-display text-xl font-medium">{b.service_title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${STATUS[b.status]}`}>{b.status.replace("_", " ")}</span>
                        {b.payment_status === "paid" && <span className="text-xs px-2 py-0.5 rounded-sm font-medium bg-green-100 text-green-800">{b.payment_model === "deposit" ? "deposit paid" : "paid"}</span>}
                        {b.proof?.customer_confirmed && <span className="text-xs px-2 py-0.5 rounded-sm font-medium bg-blue-100 text-blue-800 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />verified</span>}
                      </div>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{b.date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{b.start_time}–{b.end_time}</span>
                        <span className="flex items-center gap-1"><User className="w-4 h-4" />{b.subscriber_name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-display text-lg font-semibold text-primary">${Number(b.total_price ?? b.price).toFixed(2)}</span>
                      {(b.payment_model === "deposit" ? b.receipt_sent : b.payment_status === "paid") && (
                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => downloadInvoice(b.id)} data-testid={`invoice-${b.id}`}><FileText className="w-3.5 h-3.5 mr-1" />{b.payment_model === "deposit" ? "Receipt" : "Invoice"}</Button>
                      )}
                      {active && (
                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}>Cancel</Button>
                      )}
                    </div>
                  </div>

                  {b.payment_model === "deposit" && (
                    <div className="mt-3 border-t border-border pt-3 text-sm" data-testid={`deposit-info-${b.id}`}>
                      <div className="flex justify-between max-w-xs"><span className="text-muted-foreground">Service total</span><span>${Number(b.total_price ?? b.price).toFixed(2)}</span></div>
                      <div className="flex justify-between max-w-xs"><span className="text-muted-foreground">Deposit paid (non-refundable)</span><span>${Number(b.deposit || 0).toFixed(2)}</span></div>
                      <div className="flex justify-between max-w-xs font-medium"><span>Balance paid to provider</span><span>{b.balance_paid_confirmed ? (b.balance_charged_online ? "Charged ✓" : "Paid ✓") : `$${Number(b.balance_due || 0).toFixed(2)}`}</span></div>
                      {!b.balance_paid_confirmed && !b.balance_card_saved && (
                        <div className="mt-2">
                          <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => secureBalance(b.id)} data-testid={`secure-balance-${b.id}`}><CreditCard className="w-3.5 h-3.5 mr-1" />Secure balance with card</Button>
                          <p className="text-xs text-muted-foreground mt-1">Optional — save a card so your provider can charge the ${Number(b.balance_due || 0).toFixed(2)} balance at check-in. Otherwise, pay directly at the time of service.</p>
                        </div>
                      )}
                      {!b.balance_paid_confirmed && b.balance_card_saved && (
                        <p className="text-xs text-green-700 mt-1 flex items-center gap-1" data-testid={`balance-card-saved-${b.id}`}><ShieldCheck className="w-3.5 h-3.5" />Card secured — your provider will charge the balance at check-in.</p>
                      )}
                      {b.balance_paid_confirmed && b.balance_charged_online && <p className="text-xs text-muted-foreground mt-1">Your balance was charged to your saved card.</p>}
                      {!b.balance_paid_confirmed && !b.balance_card_saved && <p className="text-xs text-muted-foreground mt-1">You pay the balance directly to your provider at the time of service.</p>}
                    </div>
                  )}

                  {active && b.completion_code && (
                    <div className="mt-3 border-t border-border pt-3 flex items-center gap-3" data-testid={`completion-code-${b.id}`}>
                      <KeyRound className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Your completion code — give it to your provider when the service is done</p>
                        <p className="font-display text-2xl font-semibold tracking-[0.3em]" data-testid={`code-value-${b.id}`}>{b.completion_code}</p>
                      </div>
                    </div>
                  )}

                  {needsConfirm && <ConfirmRow booking={b} onDone={load} />}
                  {b.status === "completed" && b.proof?.customer_confirmed && !b.reviewed && <ReviewRow booking={b} onDone={load} />}
                  {b.reviewed && b.review && <ReviewDisplay booking={b} />}
                  {b.proof?.rating && !b.reviewed && <p className="mt-2 text-sm text-muted-foreground">You rated this {b.proof.rating}/5 ★</p>}

                  {b.settlement && (
                    <div className="mt-3 border-t border-border pt-3" data-testid={`settlement-${b.id}`}>
                      <p className="text-sm font-medium">Service call summary</p>
                      <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                        <div className="flex justify-between max-w-xs"><span>{b.payment_model === "deposit" ? "Base" : "Base fee (paid)"}</span><span>${Number(b.settlement.base_credited).toFixed(2)}</span></div>
                        {b.settlement.line_items && b.settlement.line_items.length > 0 ? (
                          b.settlement.line_items.map((li, i) => (
                            <div key={i} className="flex justify-between max-w-xs" data-testid={`settlement-item-${b.id}-${i}`}>
                              <span>{li.description || (li.kind === "labor" ? "Labor" : "Part")} <span className="text-[10px] uppercase tracking-wide opacity-60">{li.kind}</span></span>
                              <span>${Number(li.amount).toFixed(2)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="flex justify-between max-w-xs"><span>Parts + labor</span><span>${(Number(b.settlement.parts) + Number(b.settlement.labor)).toFixed(2)}</span></div>
                        )}
                        <div className="flex justify-between max-w-xs font-medium text-foreground border-t border-border pt-0.5"><span>Final total</span><span>${Number(b.settlement.final_total).toFixed(2)}</span></div>
                      </div>
                      {b.settlement.note && <p className="text-xs text-muted-foreground mt-1">Note: {b.settlement.note}</p>}
                      {b.settlement.status === "invoiced" && Number(b.settlement.balance_due) > 0 ? (
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-sm font-semibold text-primary">Balance due ${Number(b.settlement.balance_due).toFixed(2)}</span>
                          <Button size="sm" className="rounded-sm" onClick={() => payBalance(b.id)} data-testid={`pay-balance-${b.id}`}>Pay balance</Button>
                        </div>
                      ) : b.settlement.status === "direct" ? (
                        <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-sm font-medium bg-amber-100 text-amber-800" data-testid={`settlement-direct-${b.id}`}>Balance ${Number(b.settlement.balance_due).toFixed(2)} paid directly to provider</span>
                      ) : (
                        <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-sm font-medium bg-green-100 text-green-800" data-testid={`settlement-paid-${b.id}`}>Paid in full</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
